(ns common.widgets.upload
  (:require ["@mui/material/Backdrop$default" :as Backdrop]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/CircularProgress$default" :as CircularProgress]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/styles" :as styles]
            ["react-use/lib/useTimeout$default" :as useTimeout]
            [clojure.string :as str]
            [com.rpl.specter :refer [ALL transform]]
            [common.widgets.generic-upload :as generic-upload]
            [lemonpi.content-function.shared.macro :as m]
            [manage.content-configuration-page.placeholder-expression.macro-input :refer [macro-input]]
            [mate.core :as mc]
            [uix.core :as uix :refer [$ defui]]))

(defui pending-overlay []
  ($ Backdrop
     {:open true
      :data-testid "progressBar"
      :sx #js {:position "absolute"
               :zIndex 100
               :borderRadius 1
               :backgroundColor "rgba(0, 0, 0, 0.2) !important"}}
     ($ CircularProgress)))

(defui timed-overlay
  [{:keys [timeout message background-color color icon]}]
  ;; Display for x ms and then hide
  (let [[hidden _cancel] (useTimeout timeout)]
    (when-not (hidden)
      ($ Backdrop
         {:open true
          :sx #js {:position "absolute"
                   :zIndex 100
                   :flexDirection "column"
                   :borderRadius 1
                   :backgroundColor (str background-color "!important")}}
         ($ Icon
            {:sx #js {:color color}}
            icon)
         message))))

(defui overlay
  [{:keys [state success-message failure-message]}]
  (let [theme (styles/useTheme)]
    (case state
      :pending ($ pending-overlay)
      :success ($ timed-overlay
                  {:background-color (.. theme -palette -success -background)
                   :color "success.main"
                   :icon "done"
                   :message (or success-message "Successfully saved")
                   :timeout 1000})
      :failure ($ timed-overlay
                  {:background-color (.. theme -palette -error -background)
                   :color "error.main"
                   :icon "clear"
                   :message (or failure-message "Something went wrong")
                   :timeout 2000})
      nil)))

(def mimes
  {"jpg" ["image/jpeg"]
   "png" ["image/png"]
   "gif" ["image/gif"]
   "mp4" ["video/mp4"]
   "mov" ["video/quicktime"]
   "webm" ["video/webm"]
   "mp3" ["audio/mpeg"]
   "wav" ["audio/wav"]
   "svg" ["image/svg+xml"]
   "zip" ["application/zip", "application/x-zip-compressed", "application/octet-stream", "multipart/x-zip"]
   "json" ["application/json"]
   "csv" ["text/csv" "application/vnd.ms-excel"]})

(defn guess-type
  [s]
  (cond
    (= js/File (type s)) :file
    (string? s) (cond
                  (->> s
                       (m/parse-macro-expr)
                       (some m/macro?)) :macro
                  (re-matches #"https?://.+" s) :url)))

(defn slot-type
  [slot]
  (guess-type (:value slot)))

(defui placeholder-image [{:keys [icon-type]}]
  ($ :div {:class "placeholder-image"}
     ($ :span {:class "placeholder-image__text"}
        "No file uploaded")
     ($ :i {:class "material-icons placeholder-image__icon"}
        icon-type)))

(defui macro-image [{:keys [icon-type]}]
  ($ :div {:class "macro-image"}
     ($ :span {:class "macro-image__text"}
        "Preview not available via macro")
     ($ :i {:class ["material-icons" "macro-image__icon"]}
        icon-type)))

(defn revoke-url
  [url]
  (.. js/window -URL (revokeObjectURL url)))

(defn create-url
  [file]
  (.. js/window -URL (createObjectURL file)))

(defprotocol FileUpload
  (init [this extensions value])
  (value [this]))

(defrecord SingleFileUpload [slots]
  FileUpload
  (init [this extensions value]
    (assoc this :slots [{:id 1
                         :extensions extensions
                         :accept (mapcat mimes extensions)
                         :value value}]))
  (value [this]
    (:value (first (:slots this)))))

(defrecord MultiFileUpload [slots]
  FileUpload
  (init [this extensions value]
    (let [n (atom 0)]
      (assoc this :slots (reduce (fn [acc ext]
                                   (let [value (get value ext)]
                                     (conj acc {:id (swap! n inc)
                                                :extensions [ext]
                                                :accept (mimes ext)
                                                :value value})))
                                 []
                                 extensions))))
  (value [this]
    (map (fn [{:keys [extensions value]}]
           {(first extensions) value})
         (:slots this))))

(defn event-handler
  [state [event-name & args]]
  (letfn [(match-slot? [slot]
            (fn [x] (= (:id slot) (:id x))))
          (slot-accepts? [mime]
            (fn [x] (some #(= mime %) (:accept x))))]
    (case event-name
      :start-edit (let [[slot] args]
                    (transform [:files :slots ALL (match-slot? slot)]
                               #(assoc %
                                       :edit? true
                                       :edit-value (when (or (= :macro (slot-type %))
                                                             (= :url (slot-type %)))
                                                     (:value %))
                                       :pre %)
                               state))
      :cancel-edit (let [[slot] args]
                     (transform [:files :slots ALL (match-slot? slot)]
                                :pre
                                state))
      :accept-edit (let [[slot value set-overlay-state allow-macro?] args
                         proposed-slot (assoc slot :value value)
                         proposed-slot-type (slot-type proposed-slot)
                         update-state-fn (fn []
                                           (transform [:files :slots ALL (match-slot? slot)]
                                                      #(assoc %
                                                              :edit? false
                                                              :edit-value value
                                                              :value value)
                                                      state))]
                     (cond
                       (and (not allow-macro?) (not= :macro proposed-slot-type))
                       (do
                         ((:on-change state) value)
                         (update-state-fn))

                       (and allow-macro? proposed-slot-type)
                       (do
                         ((:on-change state) value)
                         (update-state-fn))

                       (nil? proposed-slot-type)
                       (do
                         (set-overlay-state :failure nil "URL/macro is invalid")
                         state)

                       (and (not allow-macro?) (= :macro proposed-slot-type))
                       (do
                         (set-overlay-state :failure nil "Macros cannot be used in the default content")
                         state)))
      :remove (let [[slot] args]
                (when-let [on-clear-fn (:on-clear state)]
                  (on-clear-fn))
                (revoke-url (:preview-url slot))
                (transform [:files :slots ALL (match-slot? slot)]
                           #(assoc % :value "")
                           state))
      :set-files (let [[selection] args
                       add-file (fn [acc f]
                                  (transform [:slots ALL (slot-accepts? (.-type f))]
                                             #(assoc %
                                                     :edit? false
                                                     :value f)
                                             acc))
                       new-files (reduce add-file (:files state) selection)]
                   (when (not= (:files state) new-files)
                     ((:on-change state) (value new-files)))
                   (assoc state :files new-files)))))

(defn emit
  [set-state e]
  (set-state (fn [state] (event-handler state e))))

(defui url-input
  [{:keys [slot emit-fn set-overlay-state allow-macro? edit-value set-edit-value]}]
  ($ macro-input {:value        (:edit-value slot)
                  :read-only?   false
                  :allow-macros? allow-macro?
                  :placeholder  "https://example.com"
                  :on-change    #(set-edit-value %)
                  :on-key-press (fn [^js e]
                                  (when-not (.-defaultPrevented e)
                                    (when (= (.-key e) "Enter")
                                      (.preventDefault e)
                                      (emit-fn [:accept-edit slot edit-value set-overlay-state allow-macro?]))))}))

(defui image-preview [{:keys [source]}]
  ($ :img {:class "image-preview__img"
           :alt "Image is missing"
           :src source}))

(defui video-preview [{:keys [source]}]
  ($ :video {:class "image-preview__video"
             :controls true
             :src source}))

(defui audio-preview [{:keys [source]}]
  ($ :audio {:class "image-preview__audio"
             :controls true
             :src source}))

(defui preview-body
  [{:keys [slot preview-component icon-type]}]
  (let [slot-value (:value slot)
        slot-type (slot-type slot)
        preview-url (uix/use-memo (fn []
                                    (when (and (= :file slot-type) slot-value)
                                      (create-url slot-value)))
                                  [slot-value slot-type])]
    (uix/use-effect (fn []
                      (fn []
                        (when preview-url
                          (revoke-url preview-url))))
                    [preview-url])

    ($ :div {:class "image-preview__body"}
       (case slot-type
         nil ($ placeholder-image {:icon-type icon-type})
         :url ($ preview-component {:source (:value slot)})
         :file ($ preview-component {:source preview-url})
         :macro ($ macro-image {:icon-type icon-type})))))

(defui preview-header
  [{:keys [slot emit-fn set-overlay-state allow-macro?] :as props}]
  (let [[edit-value set-edit-value] (uix/use-state (:value slot))
        type (slot-type slot)]
    ($ :div {:class "image-preview__header"}
       (if (:edit? slot)
         ($ :div {:class "file-link"}
            ($ url-input {:edit-value edit-value
                          :set-edit-value set-edit-value
                          :& props}))

         ($ :div {:class "image-preview__header-description"}
            ($ Stack {:direction "row"
                      :alignItems "center"
                      :gap 1}
               ($ Icon "insert_drive_file")
               ($ :div {:data-testid "image-preview__description-file-name"
                        :class ["image-preview__description-file-name"
                                (when (= :url type)
                                  "image-preview--uploaded")]}
                  (case type
                    nil (str/join ", " (map #(str "." %) (:extensions slot)))
                    :file (.-name (:value slot))
                    :url (:value slot)
                    :macro (:value slot))

                  ($ :div {:class "image-preview__description-info"}
                     (case type
                       nil "No file uploaded"
                       :file (str (Math/round (/ (.-size (:value slot)) 1000)) " kB")
                       :url "Remote file"
                       :macro "Macro"))))))
       ($ Stack {:direction "row" :alignItems "center"}
          (when (:edit? slot)
            ($ :<>
               ($ Button {:on-click #(emit-fn [:accept-edit slot edit-value set-overlay-state allow-macro?])
                          :sx #js {:minWidth "min-content"}}
                  "Save")
               ($ Button {:on-click #(emit-fn [:cancel-edit slot])
                          :sx #js {:minWidth "min-content"}}
                  "Cancel")))

          (when (and (:enable-edit? props)
                     (not (:edit? slot)))
            ($ :<>
               ($ Button {:on-click #(emit-fn [:remove slot])
                          :sx #js {:minWidth "min-content"}}
                  "Clear")
               ($ Button {:on-click #(emit-fn [:start-edit slot])
                          :sx #js {:minWidth "min-content"}}
                  "Edit")))

          (when (and (not (:enable-edit? props))
                     (:value slot))
            ($ Button {:on-click #(emit-fn [:remove slot])
                       :sx #js {:minWidth "min-content"}
                       :data-testid "remove-file-button"}
               "Remove"))))))

(defui file-list
  [{:keys [slots filetype] :& props}]
  ($ :div {:class "file-list"}
     (for [[index slot] (mc/seq-indexed slots)]
       ($ :div {:key index}
          ($ preview-header {:slot slot
                             :& props})
          (cond
            (= "video" filetype) ($ preview-body {:slot slot
                                                  :preview-component video-preview
                                                  :icon-type "videocam"})
            (= "image" filetype) ($ preview-body {:slot slot
                                                  :preview-component image-preview
                                                  :icon-type "photo"})
            (= "audio" filetype) ($ preview-body {:slot slot
                                                  :preview-component audio-preview
                                                  :icon-type "audiotrack"}))))))

(defn parse-extensions
  [s]
  (->> (str/split s #"\s*,\ ?\s*")
       (filter seq)
       (map #(str/replace % #"^\." ""))))

(defn- initial-files-state
  [extensions value type]
  (let [extensions (parse-extensions extensions)
        files (case type
                "single-file" (->SingleFileUpload [])
                "multi-file" (->MultiFileUpload []))]
    (init files extensions value)))

(defui upload
  [{:keys [onChange on-clear
           overlay-state set-overlay-state
           enableEdit enablePreview type extensions value allow-macro? filetype class error maxSize relative-box]
    :or {type "single-file"}}]
  (let [[state set-state] (uix/use-state {})
        [overlay-state' set-overlay-state'] (uix/use-state {})
        set-overlay-state (uix/use-callback
                           (fn [new-state success-message failure-message]
                             (if set-overlay-state
                               (set-overlay-state new-state success-message failure-message)
                               (set-overlay-state' (cond-> {:state new-state}
                                                     success-message (assoc :success-message success-message)
                                                     failure-message (assoc :failure-message failure-message)))))
                           [set-overlay-state])
        allow-macro? (boolean allow-macro?)
        props {:class class
               :error error
               :extensions extensions
               :emit-fn #(emit set-state %)
               :max-size maxSize
               :overlay-state overlay-state'
               :set-overlay-state set-overlay-state
               :enable-edit? enableEdit
               :filetype filetype
               :allow-macro? allow-macro?
               :multiple (= "multi-file" type)}
        extensions* (parse-extensions extensions)
        accept-mimes (mapcat mimes extensions*)]

    (uix/use-effect (fn []
                      (when overlay-state
                        (set-overlay-state' overlay-state)))
                    [overlay-state])

    (uix/use-effect (fn []
                      (set-state (fn [state] (assoc state :files (initial-files-state extensions value type)))))
                    [value extensions type filetype])

    (uix/use-effect (fn []
                      (set-state (fn [state]
                                   (assoc state
                                          :on-change (fn [value]
                                                       (when onChange
                                                         (onChange (clj->js value))))
                                          :on-clear (or on-clear identity)))))
                    [onChange on-clear])

    ($ generic-upload/upload
       {:class [class (when error "invalid")]
        :data-component :upload-component
        :on-drop #(emit set-state [:set-files (js->clj %)])
        :on-drop-rejected #(set-overlay-state :failure nil "Invalid filesize or type")
        :overlay ($ overlay overlay-state')
        :preview (when enablePreview
                   ($ file-list {:slots (:slots (:files state))
                                 :& props}))
        :accept (str/join "," accept-mimes)
        :& [(when extensions
              {:extensions extensions})
            (when maxSize
              {:max-size maxSize})
            (when relative-box
              {:relative-box relative-box})]})))
